import {
  enableProdMode,
  ErrorHandler,
  provideAppInitializer,
  inject,
} from '@angular/core';
import {
  init,
  browserTracingIntegration,
  replayIntegration,
  createErrorHandler,
  TraceService,
  setUser,
  captureConsoleIntegration,
  extraErrorDataIntegration,
} from '@sentry/angular';
import { SafeArea } from '@capacitor-community/safe-area';

import { environment } from './environments/environment';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import {
  provideAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  getAuth,
  connectAuthEmulator,
} from '@angular/fire/auth';
import { Capacitor } from '@capacitor/core';
import {
  provideDatabase,
  getDatabase,
  connectDatabaseEmulator,
} from '@angular/fire/database';
import {
  provideFirestore,
  getFirestore,
  connectFirestoreEmulator,
} from '@angular/fire/firestore';
import {
  provideStorage,
  getStorage,
  connectStorageEmulator,
} from '@angular/fire/storage';
import {
  provideFunctions,
  getFunctions,
  connectFunctionsEmulator,
} from '@angular/fire/functions';
import {
  NavigationEnd,
  PreloadAllModules,
  provideRouter,
  Router,
  RouteReuseStrategy,
  withPreloading,
} from '@angular/router';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { PurchasesService } from '@app/purchases.service';
import { bootstrapApplication } from '@angular/platform-browser';
import { routes } from '@app/routes';
import { AppComponent } from './app/app.component';
import { AnalyticsService } from '@app/analytics.service';
import { AuthService } from '@app/auth.service';
import { first } from 'rxjs';
import { setLogLevel, LogLevel } from '@angular/fire';

setLogLevel(LogLevel.SILENT);

// A workaround to avoid the status bar being black on Android
SafeArea.enable({
  config: {
    customColorsForSystemBars: true,
    statusBarColor: '#00000000', // transparent
    statusBarContent: 'light',
    navigationBarColor: '#00000000', // transparent
    navigationBarContent: 'light',
  },
});

init({
  enabled: environment.production,
  dsn: environment.sentryDsn,
  release: environment.version,
  environment: environment.production ? 'prod' : 'dev',
  ignoreErrors: [
    // RevenueCat's Capacitor plugin rejects every call with this string on web; expected on the web build.
    'Web not supported in this plugin.',
  ],
  integrations: [
    // Registers and configures the Tracing integration,
    // which automatically instruments your application to monitor its
    // performance, including custom Angular routing instrumentation
    browserTracingIntegration(),
    extraErrorDataIntegration({ depth: 3 }),
    captureConsoleIntegration({ levels: ['error', 'warn'] }),
    // Registers the Replay integration,
    // which automatically captures Session Replays
    replayIntegration({
      blockAllMedia: false,
      maskAllText: false,
      unmask: ['.sentry-unmask'],
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,

  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
});

if (environment.production) {
  enableProdMode();
}

function tractorpalBootstrap() {
  console.log('[bootstrapping]');

  // This is here to setup Sentry tracing
  inject(TraceService);

  // This is here for early initialization of the purchases service
  const purchasesService = inject(PurchasesService);
  const authService = inject(AuthService);
  const analyticsService = inject(AnalyticsService);
  const router = inject(Router);

  // This is just here to make sure the services are initialized and no lint errors are thrown
  purchasesService.initialize().then(() => {
    purchasesService.restorePurchases().catch((error) => {
      console.error('[PurchasesService.restorePurchases] Error restoring purchases', error);
    });
  });

  authService.user$.pipe(first()).subscribe(async (user) => {
    setUser({
      id: user.uid,
      email: user.email ?? undefined,
    });
    analyticsService.identify({
      id: user.uid,
      email: user.email ?? undefined,
      displayName: user.displayName ?? undefined,
    });
  });

  router.events.subscribe((event) => {
    if (event instanceof NavigationEnd) {
      analyticsService.trackPageView();
    }
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular(),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideFirebaseApp(() => {
      console.log('initializing firebase app');
      return initializeApp(environment.firebase);
    }),
    provideAuth(() => {
      const auth = Capacitor.isNativePlatform()
        ? initializeAuth(getApp(), { persistence: indexedDBLocalPersistence })
        : getAuth();

      if (environment.useEmulators) {
        connectAuthEmulator(auth, 'http://localhost:9099');
      }

      return auth;
    }),
    provideDatabase(() => {
      console.log('initializing database');
      const db = getDatabase();

      if (environment.useEmulators) {
        connectDatabaseEmulator(db, 'localhost', 9000);
      }

      return db;
    }),
    provideFirestore(() => {
      console.log('initializing firestore');
      const firestore = getFirestore();

      if (environment.useEmulators) {
        connectFirestoreEmulator(firestore, 'localhost', 8080);
      }

      return firestore;
    }),
    provideStorage(() => {
      console.log('initializing storage');
      const storage = getStorage();

      if (environment.useEmulators) {
        connectStorageEmulator(storage, 'localhost', 9199);
      }

      return storage;
    }),
    provideFunctions(() => {
      console.log('initializing functions');
      const functions = getFunctions();

      if (environment.useEmulators) {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }

      return functions;
    }),
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: true,
      }),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    provideAppInitializer(tractorpalBootstrap),
  ],
}).catch((err) => console.error(err));
